'use client';

import { type ChangeEvent, type ReactNode, useMemo } from 'react';
import { FieldGroup, type FieldGroupType } from '@/app/components/forms/field-group';
import { OptionsGroup, type OptionsGroupOrientation } from '@/app/components/forms/options-group';
import { controlCheckboxIndicatorStyled, controlCheckboxStyled, controlLabelStyled, type CustomFormStyleConfig, getCustomFormStyle, inputBoxStyled } from '@/app/components/forms/styles';
import { Icon } from '@/app/components/icons';
import { IconCheck } from '@/app/components/icons/ui-actions/icon-check';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA, type DataGAProps } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';
export interface CheckboxOption {
  name: string;
  label?: string;
  children?: ReactNode;
  checked?: boolean;
  disabled?: boolean;
}
export interface CheckboxProps extends Omit<FieldGroupType, 'length' | 'maxLength' | 'showMaxLength'> {
  orientation?: OptionsGroupOrientation;
  name: string;
  options: CheckboxOption[];
  onChangeInput?: (e: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  customStyle?: CustomFormStyleConfig;
  tracking?: Omit<DataGAProps, 'click'>;
}
export const Checkbox = ({
  className,
  orientation = 'horizontal',
  theme = 'light',
  name,
  options = [],
  error = false,
  disabled = false,
  customStyle,
  label,
  ref,
  tracking,
  onChangeInput,
  ...props
}: CheckboxProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    safeCall(onChangeInput, event);
  };
  const renderOptions = useMemo(() => {
    return options.map((option, index) => {
      const isDisabled = disabled || !!option.disabled;
      const isChecked = !!option.checked;
      const id = `${name}-${option.name}`;
      const isCheckedDisabled = isChecked && isDisabled;
      const labelClass = cn(controlLabelStyled({
        theme,
        error,
        disabled: isDisabled,
        checked: isChecked,
        checkedDisabled: isCheckedDisabled
      }), 'relative inline-flex cursor-pointer gap-x-2', {
        'py-3': !!option.label
      }, getCustomFormStyle(customStyle?.checkbox?.label, {
        error,
        disabled: isDisabled,
        checked: isChecked,
        checkedDisabled: isCheckedDisabled
      }), className);
      const inputClass = cn(inputBoxStyled({
        theme,
        error,
        disabled
      }), controlCheckboxStyled({
        theme,
        error,
        disabled: isDisabled,
        checked: isChecked,
        checkedDisabled: isCheckedDisabled
      }), getCustomFormStyle(customStyle?.checkbox?.input, {
        error,
        disabled: isDisabled,
        checked: isChecked,
        checkedDisabled: isCheckedDisabled
      }));
      const indicatorClass = cn(controlCheckboxIndicatorStyled({
        theme,
        checked: isChecked,
        checkedDisabled: isCheckedDisabled
      }), getCustomFormStyle(customStyle?.checkbox?.indicator, {
        checked: isChecked,
        checkedDisabled: isCheckedDisabled
      }));
      return <label key={option.name} htmlFor={id} ref={label ? undefined : ref} className={labelClass} {...createDataAttributes({
        test: 'label'
      })}>
          <input id={id} type="checkbox" name={option?.name} checked={isChecked} disabled={isDisabled} onChange={handleChange} aria-checked={isChecked} aria-disabled={isDisabled} className={inputClass} {...createDataAttributes({
          test: `checkbox-${index}`
        })} {...createDataGA({
          eventName: tracking?.eventName ?? undefined,
          group: tracking?.group ?? undefined,
          click: true,
          text: option?.label,
          value: option?.name
        })} />
          <Icon icon={<IconCheck />} size="md" className={indicatorClass} />
          {option.children ?? option.label}
        </label>;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, name, disabled, error, theme, className, customStyle]);
  const groupContent = options.length === 1 ? renderOptions : <OptionsGroup name={name} role="group" orientation={orientation} {...createDataAttributes({
    test: name
  })}>
        {renderOptions}
      </OptionsGroup>;
  return <FieldGroup {...props} ref={label ? ref : undefined} inputType="control" label={label} htmlFor={name} theme={theme} customStyle={customStyle?.fieldGroup} {...createDataAttributes({
    test: name
  })}>
      {groupContent}
    </FieldGroup>;
};